import apiClient from '../../shared/services/apiClient';

/**
 * LLM Service (User Version)
 *
 * Handles user-level LLM API operations.
 * Users can manage their API tokens, view usage logs and statistics.
 *
 * All routes use /api/user/llm/* prefix
 */
class LLMService {
  // ==========================================
  // Account Management
  // ==========================================

  /**
   * Get LLM account info
   */
  async getAccount() {
    try {
      const response = await apiClient.get('/api/user/llm/account');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Activate LLM service
   */
  async activateAccount() {
    try {
      const response = await apiClient.post('/api/user/llm/account/activate');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Transfer balance to LLM API quota
   * @param {number} amount - Amount to transfer (USD, from the main balance)
   * @param {number|null} userCouponId - Optional LLM top-up coupon (adds bonus quota)
   */
  async transferToQuota(amount, userCouponId = null) {
    try {
      const response = await apiClient.post('/api/user/llm/account/transfer', { amount, user_coupon_id: userCouponId || undefined });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /** LLM top-up coupons in the user's wallet */
  async getTransferCoupons() {
    try {
      const response = await apiClient.get('/api/user/llm/account/transfer-coupons');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /** Auto-transfer settings: { enabled, threshold, amount, last_at, last_error } */
  async getAutoTransfer() {
    try {
      const response = await apiClient.get('/api/user/llm/account/auto-transfer');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  async updateAutoTransfer(data) {
    try {
      const response = await apiClient.put('/api/user/llm/account/auto-transfer', data);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // ==========================================
  // Token (API Key) Management
  // ==========================================

  /**
   * Get user's available groups
   */
  async getGroups() {
    try {
      const response = await apiClient.get('/api/user/llm/groups');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // ==========================================
  // Token Management
  // ==========================================

  /**
   * Get all tokens
   * @param {object} params - Query parameters
   */
  async getTokens(params = {}) {
    try {
      const response = await apiClient.get('/api/user/llm/tokens', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Create a new token
   * @param {object} data - Token data
   */
  async createToken(data) {
    try {
      const response = await apiClient.post('/api/user/llm/tokens', data);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Update token
   * @param {number} id - Token ID
   * @param {object} data - Token data
   */
  async updateToken(id, data) {
    try {
      const response = await apiClient.put(`/api/user/llm/tokens/${id}`, data);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Update token status (enable/disable)
   * @param {number} id - Token ID
   * @param {number} status - Status (1=enabled, 2=disabled)
   */
  async updateTokenStatus(id, status) {
    try {
      const response = await apiClient.put(`/api/user/llm/tokens/${id}/status`, { id, status });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Delete token
   * @param {number} id - Token ID
   */
  async deleteToken(id) {
    try {
      const response = await apiClient.delete(`/api/user/llm/tokens/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Get a token's full key (the list only carries masked keys)
   * @param {number} id - Token ID
   */
  async getTokenKey(id) {
    try {
      const response = await apiClient.post(`/api/user/llm/tokens/${id}/key`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // ==========================================
  // Usage Logs
  // ==========================================

  /**
   * Get usage logs
   * @param {object} params - Query parameters
   */
  async getLogs(params = {}) {
    try {
      const response = await apiClient.get('/api/user/llm/logs', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Search usage logs
   * @param {object} params - Search parameters
   */
  async searchLogs(params = {}) {
    try {
      const response = await apiClient.get('/api/user/llm/logs/search', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Get log statistics
   */
  async getLogStats() {
    try {
      const response = await apiClient.get('/api/user/llm/logs/stats');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // ==========================================
  // Models
  // ==========================================

  /**
   * Get all available models
   */
  async getModels() {
    try {
      const response = await apiClient.get('/api/user/llm/models');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Get user's available models
   */
  async getUserModels() {
    try {
      const response = await apiClient.get('/api/user/llm/models/user');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Model catalog for the models page: {columns, rows} expanded to objects; prices are before the group ratio
   */
  async getModelCatalog() {
    try {
      const response = await apiClient.get('/api/user/llm/pricing', { params: { format: 'catalog' } });
      const body = response.data;
      const d = body?.data;
      if (!d || !Array.isArray(d.rows)) return body;
      const models = d.rows.map((r) => {
        const m = Object.fromEntries(d.columns.map((c, i) => [c, r[i]]));
        return { ...m, groups: String(m.groups || '').split(',').filter(Boolean), description: m.description || '', tags: m.tags || '' };
      });
      return { ...body, data: { models, vendors: d.vendors || [], groupRatio: d.group_ratio || {} } };
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Get model pricing
   */
  async getPricing() {
    try {
      // compact = {columns, rows}（~50KB，原始 ~190KB 经 CDN 边缘会被截断）；这里展开回对象数组
      const response = await apiClient.get('/api/user/llm/pricing', { params: { format: 'compact' } });
      const body = response.data;
      const d = body?.data;
      if (d && Array.isArray(d.columns) && Array.isArray(d.rows)) {
        return {
          ...body,
          data: d.rows.map((r) => Object.fromEntries(d.columns.map((c, i) => [c, r[i]]))),
        };
      }
      return body;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // ==========================================
  // Statistics
  // ==========================================

  /**
   * Get usage statistics
   * @param {object} params - Query parameters (start_timestamp, end_timestamp)
   */
  async getStats(params = {}) {
    try {
      const response = await apiClient.get('/api/user/llm/stats', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Get quota records
   */
  async getQuotaRecords() {
    try {
      const response = await apiClient.get('/api/user/llm/stats/quota');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // ==========================================
  // Playground
  // ==========================================

  /**
   * Get available models for playground
   */
  async getPlaygroundModels(type = 'all') {
    try {
      const response = await apiClient.get('/api/user/llm/playground/models', { params: { type } });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Send chat message in playground
   * @param {object} data - Chat data (model, messages, temperature, etc.)
   */
  async playgroundChat(data) {
    try {
      const response = await apiClient.post('/api/user/llm/playground/chat', data);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Generate image in playground
   * @param {object} data - Image generation data (model, prompt, size, etc.)
   */
  async playgroundImage(data) {
    try {
      const response = await apiClient.post('/api/user/llm/playground/image', data);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Generate speech audio in playground
   * @param {object} data - TTS data (model, input, voice, etc.)
   */
  async playgroundAudioSpeech(data) {
    try {
      const response = await apiClient.post('/api/user/llm/playground/audio/speech', data, {
        responseType: 'blob',
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Submit video generation task
   * @param {object} data - Video generation data (model, prompt, size, duration, etc.)
   */
  async playgroundVideo(data) {
    try {
      const response = await apiClient.post('/api/user/llm/playground/video', data, { timeout: 120000 });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Get video generation task status
   * @param {string} taskId - Task ID
   */
  async playgroundVideoStatus(taskId) {
    try {
      const response = await apiClient.get(`/api/user/llm/playground/video/${taskId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // ==========================================
  // Suno Music
  // ==========================================

  /**
   * Submit Suno music/lyrics generation task
   * @param {object} data - Suno data (action, prompt, gpt_description_prompt, mv, title, tags, make_instrumental)
   */
  async playgroundSunoSubmit(data) {
    try {
      const response = await apiClient.post('/api/user/llm/playground/suno/submit', data);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Get Suno task status
   * @param {string} taskId - Task ID
   */
  async playgroundSunoStatus(taskId) {
    try {
      const response = await apiClient.get(`/api/user/llm/playground/suno/fetch/${taskId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // ==========================================
  // Midjourney Logs
  // ==========================================

  /**
   * Get Midjourney drawing logs
   * @param {object} params - Query parameters
   */
  async getMjLogs(params = {}) {
    try {
      const response = await apiClient.get('/api/user/llm/mj-logs', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // ==========================================
  // Check-in
  // ==========================================

  /**
   * Claim a gift credit coupon straight into LLM quota
   */
  async claimGift(userCouponId) {
    try {
      const response = await apiClient.post(`/api/user/llm/account/gift/${userCouponId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Get check-in status
   */
  async getCheckinStatus() {
    try {
      const response = await apiClient.get('/api/user/llm/checkin');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Perform check-in
   */
  async doCheckin() {
    try {
      const response = await apiClient.post('/api/user/llm/checkin');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // ==========================================
  // Subscription
  // ==========================================

  /**
   * Get subscription plans
   */
  async getSubscriptionPlans() {
    try {
      const response = await apiClient.get('/api/user/llm/subscription/plans');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Get user's current subscription
   */
  async getSubscription() {
    try {
      const response = await apiClient.get('/api/user/llm/subscription');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // ==========================================
  // Redemption
  // ==========================================

  /**
   * Redeem a code for quota
   * @param {string} code - Redemption code
   */
  async redeemCode(code) {
    try {
      const response = await apiClient.post('/api/user/llm/redeem', { code });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // ==========================================
  // Task Logs (Video/Music Generation)
  // ==========================================

  /**
   * Get task logs (video generation, music generation, etc.)
   * @param {object} params - Query parameters
   */
  async getTaskLogs(params = {}) {
    try {
      const response = await apiClient.get('/api/user/llm/task-logs', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }
}

export default new LLMService();
